<template>
  <header class="h-14 sm:h-16 bg-casino-sidebar/95 backdrop-blur-md border-b border-white/5 flex items-center justify-between px-3 sm:px-6 sticky top-0 z-[9500]">
    <!-- Left - Menu Button + Search -->
    <div class="flex items-center gap-2 sm:gap-4 flex-1">
      <!-- Mobile Menu Button -->
      <button
        @click="$emit('toggle-sidebar')"
        class="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
        :class="{ 'text-casino-primary': isSidebarOpen }"
      >
        <Menu class="w-5 h-5 sm:w-6 sm:h-6" />
      </button>

      <!-- Mobile Logo - visible solo en movil, el sidebar lo muestra en desktop -->
      <RouterLink to="/" class="lg:hidden flex items-center gap-2 flex-shrink-0">
        <img src="/todofino_icon.png" alt="TodoFino" class="w-7 h-7 rounded-lg object-cover" />
        <img src="/todofino_logo.png" alt="TodoFino.bet" class="h-5" />
      </RouterLink>

      <!-- Desktop Expand Sidebar Button - Only when collapsed -->
      <button
        v-if="!isSidebarOpen"
        @click="$emit('toggle-sidebar')"
        class="hidden lg:flex p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
        :title="t('sidebar.expand')"
      >
        <PanelLeftOpen class="w-5 h-5" />
      </button>

      <!-- OCULTO: search no funcional. El input no tiene handler ni v-model conectado a filtrado.
      <div class="relative flex items-center">
        <button
          @click="openSearch"
          class="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
          :class="{ 'text-casino-primary': searchOpen }"
        >
          <Search class="w-5 h-5" />
        </button>
        
        <div
          v-if="searchOpen"
          class="lg:hidden fixed left-3 right-3 top-14 z-[9600]"
        >
          <div class="bg-casino-card border border-white/10 rounded-xl shadow-2xl p-3">
            <div class="relative">
              <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                ref="searchInputRef"
                v-model="searchQuery"
                type="text"
                placeholder="Buscar juegos..."
                class="w-full bg-casino-dark border border-white/20 rounded-lg pl-11 pr-4 py-3 text-base text-white placeholder-gray-500 focus:outline-none focus:border-casino-primary/50 transition-all"
              />
            </div>
            <div class="mt-2 flex gap-2 overflow-x-auto pb-1">
              <button
                v-for="tag in ['Slots', 'Ruleta', 'Blackjack']"
                :key="tag"
                class="px-3 py-1 bg-white/5 rounded-full text-xs text-gray-400 hover:text-white hover:bg-white/10 whitespace-nowrap"
                @mousedown.prevent
                @click="searchQuery = tag"
              >
                {{ tag }}
              </button>
            </div>
          </div>
        </div>
        
        <div class="hidden lg:block relative w-64 xl:w-80">
          <Search class="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Buscar juegos..."
            class="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-casino-primary/50 transition-all"
          />
        </div>
      </div>
      -->
    </div>

    <!-- Right - Actions with more breathing room -->
    <div class="flex items-center gap-2 sm:gap-4 pl-2">
      <!-- Wallet Button - Compact on mobile -->
      <button
        v-if="authStore.isAuthenticated"
        @click="openWalletModal('deposit')"
        class="flex items-center gap-1.5 sm:gap-3 bg-white/5 border border-white/10 rounded-lg sm:rounded-xl px-2 sm:px-4 py-1.5 sm:py-2 hover:bg-white/10 hover:border-white/20 transition-all group"
      >
        <Wallet class="w-4 h-4 sm:w-5 sm:h-5 text-casino-win flex-shrink-0" />
        <div class="hidden sm:flex flex-col text-left leading-tight min-w-[112px]">
          <div class="flex items-center justify-between gap-3">
            <span class="text-[10px] text-gray-500 uppercase tracking-wide">{{ t('topbar.real') }}</span>
            <span class="text-sm font-bold text-white tabular-nums">{{ currencySymbol }}{{ formattedBalance }}</span>
          </div>
          <div class="flex items-center justify-between gap-3">
            <span class="text-[10px] text-casino-gold/80 uppercase tracking-wide">{{ t('topbar.bonus') }}</span>
            <span class="text-xs font-semibold text-casino-gold tabular-nums">{{ currencySymbol }}{{ formattedBonusBalance }}</span>
          </div>
        </div>
        <div class="sm:hidden flex flex-col leading-tight text-left">
          <span class="text-sm font-bold text-white tabular-nums">{{ currencySymbol }}{{ formattedBalanceCompact }}</span>
          <span class="text-[10px] font-semibold text-casino-gold tabular-nums">{{ t('topbar.bonusShort') }} {{ currencySymbol }}{{ formattedBonusBalanceCompact }}</span>
        </div>
      </button>

      <!-- Auth Buttons -->
      <template v-if="!authStore.isAuthenticated">
        <button
          @click="$emit('open-login')"
          class="px-4 py-2 text-xs sm:text-sm font-medium text-gray-300 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
        >
          {{ t('topbar.login') }}
        </button>
        <button
          @click="$emit('open-register')"
          class="px-4 py-2 text-xs sm:text-sm font-semibold bg-casino-primary hover:bg-casino-primary-light text-white rounded-lg transition-colors"
        >
          {{ t('topbar.join') }}
        </button>
      </template>

      <!-- Logged In User - Compact on mobile -->
      <template v-else>
        <div class="relative" ref="bonusMenuRef">
          <button
            class="relative p-1.5 text-gray-400 hover:text-white transition-colors"
            :class="{ 'text-casino-gold': showBonusMenu || bonusStore.claimableCount > 0 }"
            @click="toggleBonusMenu"
          >
            <Bell class="w-4 h-4" />
            <span
              v-if="bonusStore.claimableCount > 0"
              class="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-casino-gold text-black text-[10px] font-bold flex items-center justify-center leading-none"
            >
              {{ bonusStore.claimableCount }}
            </span>
          </button>

          <div
            v-if="showBonusMenu"
            class="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1.5rem)] bg-casino-card border border-white/10 rounded-xl shadow-xl overflow-hidden z-[9600]"
          >
            <div class="p-4 border-b border-white/5 flex items-center justify-between">
              <div>
                <p class="text-sm font-semibold text-white">{{ t('topbar.bonuses') }}</p>
                <p class="text-xs text-gray-500">{{ t('topbar.pendingActivation', { count: bonusStore.claimableCount }) }}</p>
              </div>
              <button class="btn-icon p-1.5" :disabled="bonusStore.loading" @click="bonusStore.fetchBonuses({ force: true })">
                <RefreshCw class="w-4 h-4" :class="{ 'animate-spin': bonusStore.loading }" />
              </button>
            </div>

            <div class="max-h-80 overflow-y-auto p-2">
              <div v-if="bonusStore.loading && !bonusStore.hasBonuses" class="p-3 space-y-2">
                <div class="skeleton h-4 w-44"></div>
                <div class="skeleton h-4 w-32"></div>
                <div class="skeleton h-9 w-full"></div>
              </div>

              <div v-else-if="bonusStore.recentBonuses.length === 0" class="p-4 text-center">
                <Gift class="w-8 h-8 text-gray-500 mx-auto mb-2" />
                <p class="text-sm font-medium text-white">{{ t('topbar.noBonusesTitle') }}</p>
                <p class="text-xs text-gray-500">{{ t('topbar.noBonusesText') }}</p>
              </div>

              <template v-else>
                <div
                  v-for="bonus in bonusStore.recentBonuses"
                  :key="bonus.id"
                  class="rounded-lg p-3 hover:bg-white/5 transition-colors"
                >
                  <div class="flex items-start gap-3">
                    <div class="w-9 h-9 rounded-lg bg-casino-gold/10 flex items-center justify-center flex-shrink-0">
                      <Gift class="w-4 h-4 text-casino-gold" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-2 mb-1">
                        <span class="text-[10px] px-2 py-0.5 rounded bg-white/10 text-gray-300">{{ tEnum('bonuses.type', bonus.type) }}</span>
                        <span class="text-[10px] px-2 py-0.5 rounded" :class="bonus.status === 'ASSIGNED' ? 'bg-casino-gold/20 text-casino-gold' : 'bg-casino-win/20 text-casino-win'">
                          {{ tEnum('bonuses.status', bonus.status) }}
                        </span>
                      </div>
                      <p class="text-sm font-semibold text-white truncate">{{ bonus.templateName }}</p>
                      <p class="text-xs text-gray-500">{{ formatBonusAmount(bonus) }}</p>
                    </div>
                  </div>
                  <button
                    v-if="isClaimableBonus(bonus)"
                    class="mt-3 w-full btn-primary btn-sm"
                    :disabled="bonusStore.acceptingId === bonus.id"
                    @click="claimBonusFromMenu(bonus)"
                  >
                    {{ bonusStore.acceptingId === bonus.id ? t('bonuses.activating') : t('bonuses.activate') }}
                  </button>
                </div>
              </template>
            </div>

            <RouterLink
              to="/bonuses"
              class="block p-3 border-t border-white/5 text-center text-sm font-medium text-casino-primary-light hover:bg-white/5"
              @click="showBonusMenu = false"
            >
              {{ t('topbar.viewAllBonuses') }}
            </RouterLink>
          </div>
        </div>

        <div class="relative" ref="userMenuRef">
          <button 
            @click="showUserMenu = !showUserMenu"
            class="flex items-center pl-0.5 pr-0.5 py-0.5 rounded-full hover:bg-white/5 transition-colors"
          >
            <div class="w-6 h-6 rounded-full bg-gradient-to-br from-casino-primary to-casino-primary-light flex items-center justify-center text-white font-semibold text-xs">
              {{ authStore.playerName?.charAt(0) || 'G' }}
            </div>
            <div class="hidden lg:block text-left">
              <p class="text-sm font-medium text-white">{{ authStore.playerName || t('topbar.guest') }}</p>
              <p class="text-xs text-gray-500">{{ t('topbar.player') }}</p>
            </div>
            <ChevronDown class="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 hidden sm:block" :class="{ 'rotate-180': showUserMenu }" />
          </button>

          <!-- Dropdown -->
          <div 
            v-if="showUserMenu"
            class="absolute right-0 mt-2 w-48 sm:w-56 bg-casino-card border border-white/10 rounded-xl shadow-xl overflow-hidden z-[9600]"
          >
            <div class="p-3 sm:p-4 border-b border-white/5">
              <p class="text-sm font-semibold text-white">{{ authStore.playerName || t('topbar.guest') }}</p>
              <p class="text-xs text-gray-500">{{ authStore.player?.email || 'demo@casino.com' }}</p>
            </div>
            <nav class="p-2">
              <button
                @click="openWalletModal('deposit'); showUserMenu = false"
                class="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <Wallet class="w-4 h-4" />
                {{ t('topbar.wallet') }}
              </button>
              <RouterLink
                to="/profile"
                class="flex items-center gap-3 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <User class="w-4 h-4" />
                {{ t('sidebar.profile') }}
              </RouterLink>
              <RouterLink
                to="/history"
                class="flex items-center gap-3 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <History class="w-4 h-4" />
                {{ t('sidebar.history') }}
              </RouterLink>
              <div class="border-t border-white/5 my-2"></div>
              <button
                @click="handleLogout"
                class="w-full flex items-center gap-3 px-3 py-2 text-sm text-casino-loss hover:text-casino-loss-dark hover:bg-casino-loss/10 rounded-lg transition-colors"
              >
                <LogOut class="w-4 h-4" />
                {{ t('topbar.logout') }}
              </button>
            </nav>
          </div>
        </div>
      </template>
    </div>

    <!-- Wallet Modal -->
    <WalletModal
      :is-open="showWalletModal"
      :initial-tab="walletModalTab"
      @close="showWalletModal = false"
      @success="handleWalletSuccess"
    />
  </header>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useBonusStore } from '@/stores/bonus'
import { useGamesStore } from '@/stores/games'
import WalletModal from '@/components/WalletModal.vue'
import { useI18n } from 'vue-i18n'
import { tEnum } from '@/i18n'
import { 
  Search, 
  Wallet, 
  Bell, 
  Gift,
  RefreshCw,
  ChevronDown, 
  User, 
  LogOut, 
  History,
  Menu,
  PanelLeftOpen,
  X
} from 'lucide-vue-next'
import { nextTick } from 'vue'

const emit = defineEmits(['open-login', 'open-register', 'toggle-sidebar'])

const props = defineProps({
  isSidebarOpen: {
    type: Boolean,
    default: false
  }
})

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const bonusStore = useBonusStore()
const gamesStore = useGamesStore()

const showUserMenu = ref(false)
const showBonusMenu = ref(false)
const showWalletModal = ref(false)
const walletModalTab = ref('deposit')
const userMenuRef = ref(null)
const bonusMenuRef = ref(null)
const searchOpen = ref(false)
const searchQuery = ref('')
const searchInputRef = ref(null)

const openSearch = () => {
  searchOpen.value = !searchOpen.value
  if (searchOpen.value) {
    nextTick(() => {
      searchInputRef.value?.focus()
    })
  }
}

const closeSearch = () => {
  searchOpen.value = false
  searchQuery.value = ''
}

const displayBalance = computed(() => {
  const liveBalance = Number(gamesStore.balance)
  if (gamesStore.balanceLoaded && Number.isFinite(liveBalance)) {
    return liveBalance
  }

  const cachedBalance = Number(authStore.player?.balance)
  if (Number.isFinite(cachedBalance)) {
    return cachedBalance
  }

  return null
})

const formattedBalance = computed(() => (
  displayBalance.value === null ? '--' : displayBalance.value.toFixed(2)
))

const displayBonusBalance = computed(() => {
  const liveBonus = Number(gamesStore.bonus)
  if (gamesStore.balanceLoaded && Number.isFinite(liveBonus)) {
    return liveBonus
  }

  const cachedBonus = Number(authStore.player?.bonus)
  if (Number.isFinite(cachedBonus)) {
    return cachedBonus
  }

  return null
})

const formattedBonusBalance = computed(() => (
  displayBonusBalance.value === null ? '--' : displayBonusBalance.value.toFixed(2)
))

const currencySymbol = computed(() => {
  const currency = gamesStore.currency || authStore.player?.currency || 'ARS'
  const symbols = { ARS: '$', USD: '$', EUR: '€', BRL: 'R$', MXN: '$', CLP: '$', COP: '$' }
  return symbols[currency] || '$'
})

const formattedBalanceCompact = computed(() => (
  displayBalance.value === null ? '--' : displayBalance.value.toFixed(0)
))

const formattedBonusBalanceCompact = computed(() => (
  displayBonusBalance.value === null ? '--' : displayBonusBalance.value.toFixed(0)
))

const openWalletModal = (tab) => {
  walletModalTab.value = tab
  showWalletModal.value = true
}

const isClaimableBonus = (bonus) => (
  bonus?.status === 'ASSIGNED'
  && bonus?.OPTIN === true
  && bonus?.accepted !== true
)

const formatBonusAmount = (bonus) => {
  const amount = Number(bonus?.bonusAmount || 0)
  const currency = bonus?.currency || 'ARS'
  if (bonus?.type === 'FREESPIN') {
    return t('bonuses.freespinsCount', { count: bonus.freespinsRemaining || bonus.freespinsTotal || 0 })
  }
  return `${amount.toLocaleString('es-AR', { maximumFractionDigits: 2 })} ${currency}`
}

const toggleBonusMenu = async () => {
  showBonusMenu.value = !showBonusMenu.value
  if (showBonusMenu.value) {
    showUserMenu.value = false
    await bonusStore.fetchBonuses({ force: true })
  }
}

const claimBonusFromMenu = async (bonus) => {
  await bonusStore.acceptBonus(bonus.id)
}

const handleWalletSuccess = async () => {
  await gamesStore.fetchBalance()
  await bonusStore.fetchBonuses({ force: true })
}

const handleLogout = () => {
  authStore.logout()
  showUserMenu.value = false
  router.push('/')
}

const handleClickOutside = (event) => {
  if (userMenuRef.value && !userMenuRef.value.contains(event.target)) {
    showUserMenu.value = false
  }
  if (bonusMenuRef.value && !bonusMenuRef.value.contains(event.target)) {
    showBonusMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
